<script lang="ts">
import { computed, defineComponent, ref } from 'vue'
import type { FileObject } from '@/types/accessor/indexedDB/disasterEmergencyPhotoDB'
import { ERROR_CODES } from '@/constants/error/errorCode'
import { GlobalError } from '@/utils/globalError/GlobalError'

export default defineComponent({
  name: 'PhotographyComp',
  components: {},
  props: {
    itemName: {
      type: String,
      required: true
    },
    imageCount: {
      type: Number,
      default: 0,
      required: false
    },
    imageCountLimit: {
      type: Number,
      required: true
    }
  },
  emits: ['onImageSelected'],
  setup(props, { emit }) {
    const fileDlg = ref<HTMLInputElement | null>(null)
    // 撮影された画像の上限に達したかを判定するフラグ
    const isUpperLimit = ref<boolean>(false)
    // 画像枚数
    const imageCount = computed(() => props.imageCount)

    /**
     * ファイルが選択された際の処理
     * @param e イベントオブジェクト
     * @returns なし
     */
    const onChangeFile = (e: Event): void => {
      const target = e.target as HTMLInputElement
      if (!target.files || target.files.length === 0) {
        console.warn('No file selected')
        return
      }

      const selectedFile = target.files[0]

      const emitData: FileObject = {
        image: selectedFile,
        mime: selectedFile.type
      }

      readFile(selectedFile)
        .then(() => {
          // ファイルの読み込みが完了した場合にイベントを発火
          emit('onImageSelected', emitData)
          // 同じファイルを再度選択できるようにファイル入力をリセット
          if (fileDlg.value) {
            fileDlg.value.value = ''
          }
        })
        .catch(error => {
          console.error('Error reading file:', error)
          throw new GlobalError([ERROR_CODES.COMMON_READ_FILE_FAILED])
        })
    }

    /**
     * ファイルを読み込むメソッド
     * @param file 選択されたファイル
     * @returns Promise<string | ArrayBuffer | null>
     */
    const readFile = (file: File): Promise<string | ArrayBuffer | null> => {
      return new Promise((resolve, reject) => {
        const fileReader = new FileReader()

        fileReader.onload = () => {
          resolve(fileReader.result)
        }

        fileReader.onerror = e => {
          reject(e)
        }

        fileReader.readAsDataURL(file)
      })
    }

    /**
     * 撮影された画像が上限を超えた場合にアラートを表示する
     * @param なし
     * @returns なし
     */
    const showAlert = () => {
      if (imageCount.value >= props.imageCountLimit) {
        isUpperLimit.value = true
        throw new GlobalError([ERROR_CODES.COMMON_PHOTO_LIMIT_EXCEEDED])
      }
      isUpperLimit.value = false
    }

    // 定義された関数やデータを返却
    return {
      fileDlg,
      isUpperLimit,
      onChangeFile,
      showAlert
    }
  }
})
</script>

<template>
  <label
    class="camera-container btn d-flex justify-content-center align-items-center shadow rounded-3 px-4 py-3 fw-bold"
    type="button"
    :disabled="isUpperLimit"
    @click="showAlert"
  >
    <label class="">
      <font-awesome-icon icon="camera" class="camera-icon" />
      <input
        type="file"
        :id="'imageUpload' + itemName"
        accept="image/*"
        capture="environment"
        ref="fileDlg"
        @change="onChangeFile"
        :disabled="isUpperLimit"
        hidden
      />
    </label>
    <span class="fs-4 ms-3">写真を撮影する</span>
  </label>
</template>

<style scoped>
.camera-container {
  background-color: #2c52cf;
  color: #ffffff;
  &:hover {
    background-color: #1c3ba0;
    color: #ffffff;
  }
}
/* アイコンサイズを調整 */
.camera-icon {
  font-size: 3.5rem;
  /* アイコンを拡大 */
}
</style>
